import React, { useRef, useMemo, FC } from 'react';
import { IUserJson } from '@scale/shared/types/user';
import { useQuery } from 'react-query';
import { getProjectCollections, getHostedCollections, lookupUserIdsOrEmails } from '../api';
import { useCreateWebinarSession } from '../hooks/useCreateWebinarSessionStore';
import { CancelationModal } from './CancelationModal';
import { AddSessionsModal } from './AddSessionsModal';
import { EditSessionsModal } from './EditSessionsModal';
import { ViewSessionsModal } from './ViewSessionsModal';
import { findNextSession, formatCollections, filterElapsedSessions } from '../helpers';
import _ from 'lodash';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
import { ZOOM_SERVICE_ACCOUNT_TO_HOST_KEY } from '@scale/shared/webinarSessions/types';
import { Switch, Route, useHistory, useRouteMatch, Link as RouterLink } from 'react-router-dom';
import {
  DataTable,
  Flex,
  Text,
  Link as RadixLink,
  Icon,
  IconButton,
  Skeleton,
  Tooltip,
  Badge,
} from '@scale/scaleui-radix';
import {
  faCopy,
  faArrowUpRightFromSquare,
  faPencil,
  faPlus,
  faDollarSign,
  faCancel,
  faOctagonExclamation,
} from '@fortawesome/pro-regular-svg-icons';
import CopyToClipboard from 'react-copy-to-clipboard';
import { toast } from 'react-toastify';
import { EndPreviousSessionModal } from 'react-scale/components/WebinarSessions/components/EndPreviousSessionModal';
import moment from 'moment-timezone';

interface ScheduledSessionsTableProps {
  user: IUserJson;
  showElapsedSessions: boolean;
  projectId?: string;
}

const COLUMN_INFO = [
  {
    id: 'collectionName',
    header: 'Name (# of sessions)',
    accessor: 'collectionName',
    enableSorting: true,
  },
  {
    id: 'dateAndTimes',
    header: 'Dates & Times',
    accessor: 'dateAndTimes',
    enableSorting: true,
  },
  {
    id: 'hosts',
    header: 'Hosts',
    accessor: 'hosts',
    enableSorting: false,
    size: 160,
  },
  {
    id: 'zoomJoin',
    header: 'Zoom URL',
    accessor: 'zoomJoin',
    enableSorting: false,
  },
  {
    id: 'description',
    header: 'Description',
    accessor: 'description',
    enableSorting: false,
  },
  {
    id: 'actions',
    size: 100,
    header: 'Actions',
    accessor: 'actions',
    enableSorting: false,
  },
];

type DataTableRow = {
  collectionName: JSX.Element;
  dateAndTimes: JSX.Element;
  hosts: JSX.Element;
  zoomJoin: JSX.Element;
  description: JSX.Element;
  actions: JSX.Element;
};

const columnHelper = DataTable.createColumnHelper<DataTableRow>();

const dataTableColumns = COLUMN_INFO.map(column =>
  columnHelper.accessor(column.accessor as keyof DataTableRow, {
    id: column.id,
    size: column.size,
    header: function HeaderCell() {
      return <DataTable.CellContentDefault>{column.header}</DataTable.CellContentDefault>;
    },
    enableSorting: column.enableSorting,
  }),
);

const CopyButton: FC<{ label: string; value: string }> = ({ label, value }) => {
  return (
    <CopyToClipboard
      text={value}
      onCopy={() =>
        toast(`${label} ${value} copied to clipboard`, {
          type: 'success',
          autoClose: 1000,
          position: 'top-right',
        })
      }
    >
      <IconButton color="neutral" variant="ghost" size="1">
        <Icon icon={faCopy} label="Copy" size="1" />
      </IconButton>
    </CopyToClipboard>
  );
};

export const ScheduledSessionsTable: React.FC<ScheduledSessionsTableProps> = ({
  user,
  showElapsedSessions,
  projectId,
}) => {
  const { createdNewSessions } = useCreateWebinarSession();
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const godMode = useUnleashFeatureFlag('webinar-sessions-god-mode', { userId: user._id }, false);

  const { data, isLoading } = useQuery(
    ['webinar-sessions-hosted-collections', user._id, createdNewSessions, projectId ?? 1],
    () => (projectId ? getProjectCollections(projectId) : getHostedCollections()),
    {
      staleTime: 60 * 60 * 1000,
    },
  );

  const rows = useMemo(() => {
    const collections = data?.collections ?? {};
    return showElapsedSessions
      ? formatCollections(collections, user._id)
      : filterElapsedSessions(collections, user._id);
  }, [data?.collections, showElapsedSessions, user._id]);

  const allHostIds = useMemo(() => {
    const hostIds = rows.flatMap(row => row.hostedBy);
    return [...new Set(hostIds)];
  }, [rows]);

  const { data: hostsData }: { data?: { [key: string]: any } } = useQuery(
    ['webinar-sessions-hosts', allHostIds],
    () => lookupUserIdsOrEmails(allHostIds),
    {
      enabled: allHostIds.length > 0,
    },
  );

  const match = useRouteMatch();
  const history = useHistory();
  const basename = match.path.replace(/\/$/, '');

  const linkHelper = (path: string) =>
    `${match.url.replace(/\/$/, '')}${path}${history.location.search}`;

  // eslint-disable-next-line complexity
  const dataTableContent = rows.map((row: any) => {
    const session = findNextSession(row.sessions);
    const notHost = !_.flatten(row.sessions.map((s: any) => s.hostedBy)).includes(user._id);
    const rowHosts = row.hostedBy
      .map((hostId: string) => hostsData?.find((host: any) => host.id === hostId))
      .filter(Boolean);

    const start = session ? moment.tz(session.eventStart, 'UTC') : null;
    const end = session && start ? start.clone().add(session.duration, 'minutes') : null;
    const inProgress = start && end && moment().isBetween(start, end);

    return {
      collectionName: (
        <Flex direction="column" gap="2" className="overflow-x-auto scrollbar-hide">
          <RadixLink color="accent" underline="always" asChild>
            <RouterLink to={linkHelper(`/${row._id}`)}>
              <Text style={{ whiteSpace: 'pre-wrap' }}>
                {row.name} ({row.occurrences.length})
              </Text>
            </RouterLink>
          </RadixLink>
          <Flex gap="1" align="center">
            <Badge size="1">{row._id}</Badge>
            <CopyButton label="Session id:" value={row._id} />
          </Flex>
        </Flex>
      ),
      dateAndTimes: (
        <Flex direction="column" gap="1" className="overflow-x-auto scrollbar-hide">
          {row.occurrences.map((d: Date, i: number) => {
            const date = new Date(d);
            return (
              <Text key={`${row._id}-occurrence-${i}`}>
                {'• '}
                {date.toLocaleString('en-US', {
                  weekday: 'short',
                  month: 'numeric',
                  day: 'numeric',
                  year: 'numeric',
                  hour: 'numeric',
                  minute: 'numeric',
                  timeZoneName: 'short',
                })}{' '}
                {row.sessions[i].canceled ? '(Canceled)' : ''}
              </Text>
            );
          })}
        </Flex>
      ),
      hosts: (
        <Flex direction="column" gap="1" className="overflow-x-auto scrollbar-hide">
          {rowHosts.map((host: any, i: number) => (
            <Flex key={`${row._id}-host-${i}`} gap="1" align="center" wrap="wrap">
              <Badge>{host.email}</Badge>
              <CopyButton label="Email:" value={host.email} />
            </Flex>
          ))}
        </Flex>
      ),
      zoomJoin: session ? (
        <Flex direction="column" gap="2">
          <Flex asChild align="center" gap="2">
            <RadixLink
              href={session?.zoomMeeting?.joinUrl}
              color="accent"
              underline="always"
              target="_blank"
            >
              Join meeting
              <Icon icon={faArrowUpRightFromSquare} size="1" color="accent" label="link" />
            </RadixLink>
          </Flex>
          <Tooltip content="To become the meeting host, join the meeting as a guest 5 minutes before the scheduled start, then claim host with the host key.">
            <Flex align="center" gap="1">
              <Text>Host key:</Text>
              <Badge>
                {
                  ZOOM_SERVICE_ACCOUNT_TO_HOST_KEY[
                    (session?.zoomMeeting?.host as string) ?? 'training@outlier.ai'
                  ]
                }
              </Badge>
              <CopyButton
                label="Host key:"
                value={ZOOM_SERVICE_ACCOUNT_TO_HOST_KEY[session?.zoomMeeting?.host as string]}
              />
            </Flex>
          </Tooltip>
        </Flex>
      ) : (
        <Text>All sessions have ended</Text>
      ),
      description: <Text style={{ whiteSpace: 'pre-wrap' }}>{row.description}</Text>,
      actions: (
        <Flex gap="1" className="overflow-x-auto scrollbar-hide">
          <Tooltip content={notHost && !godMode ? 'Only hosts can add sessions' : 'Add sessions'}>
            <IconButton
              color="accent"
              variant="soft"
              size="1"
              disabled={notHost && !godMode}
              asChild
            >
              <RouterLink to={linkHelper(`/${row._id}/add`)}>
                <Icon icon={faPlus} label="add" size="1" />
              </RouterLink>
            </IconButton>
          </Tooltip>
          <Tooltip content={notHost && !godMode ? 'Only hosts can edit sessions' : 'Edit sessions'}>
            <IconButton
              color="accent"
              variant="soft"
              size="1"
              disabled={notHost && !godMode}
              asChild
            >
              <RouterLink to={linkHelper(`/${row._id}/edit`)}>
                <Icon icon={faPencil} label="edit" size="1" />
              </RouterLink>
            </IconButton>
          </Tooltip>
          <Tooltip
            content={notHost && !godMode ? 'Only hosts can cancel sessions' : 'Cancel sessions'}
          >
            <IconButton
              color="accent"
              variant="soft"
              size="1"
              disabled={notHost && !godMode}
              asChild
            >
              <RouterLink to={linkHelper(`/${row._id}/cancel`)}>
                <Icon icon={faCancel} label="cancel" size="1" />
              </RouterLink>
            </IconButton>
          </Tooltip>
          <Tooltip content="Reward sessions">
            <IconButton color="accent" variant="soft" size="1">
              <RadixLink
                href={`/corp/mission-config/new?webinar=${row.sessionCollection}`}
                target="_blank"
              >
                <Icon color="accent" icon={faDollarSign} label="reward" size="1" />
              </RadixLink>
            </IconButton>
          </Tooltip>
          {(!notHost || godMode) && inProgress && (
            <Tooltip content="End previous session">
              <IconButton color="accent" variant="soft" size="1" asChild>
                <RouterLink to={linkHelper(`/${row._id}/end-previous`)}>
                  <Icon icon={faOctagonExclamation} label="end-previous-session" size="1" />
                </RouterLink>
              </IconButton>
            </Tooltip>
          )}
        </Flex>
      ),
    };
  });

  return (
    <Flex ref={tableContainerRef}>
      <Switch>
        <Route
          path={`${basename}/:id`}
          render={({ match: innerMatch, location: innerLocation, history: innerHistory }) => {
            if (!rows) {
              return null;
            }

            const currentRow = rows.find(row => row._id === innerMatch.params.id);
            if (!currentRow) {
              return null;
            }

            const closeModal = () => {
              let baseURL = innerMatch.url.split(innerMatch.params.id)[0];

              // In remoadmin, the baseURL ends up being /
              // if we trim it, history.push() does correctly reset the URL
              // and close the modal, so we need to check for it.
              if (baseURL !== '/') {
                baseURL = baseURL.replace(/\/$/, '');
              }

              return innerHistory.push(baseURL + innerLocation.search);
            };

            return (
              <Switch>
                <Route path={`${innerMatch.path}/edit`}>
                  <EditSessionsModal
                    userId={currentRow.currentUserId}
                    sessions={currentRow.sessions}
                    toggleModal={closeModal}
                  />
                </Route>
                <Route path={`${innerMatch.path}/cancel`}>
                  <CancelationModal collection={currentRow.sessions} toggleModal={closeModal} />
                </Route>
                <Route path={`${innerMatch.path}/end-previous`}>
                  <EndPreviousSessionModal
                    toggleModal={closeModal}
                    sessions={currentRow.sessions}
                  />
                </Route>
                <Route path={`${innerMatch.path}/add`}>
                  <AddSessionsModal
                    collectionId={currentRow.sessionCollection}
                    toggleModal={closeModal}
                  />
                </Route>
                <Route path={`${innerMatch.path}`}>
                  <ViewSessionsModal sessions={currentRow.sessions} toggleModal={closeModal} />
                </Route>
              </Switch>
            );
          }}
        />
      </Switch>
      <Skeleton loading={isLoading}>
        <DataTable.Root
          columns={dataTableColumns}
          data={dataTableContent}
          enableColumnFilters={false}
        >
          {({ table }) => (
            <>
              <DataTable.Table>
                <DataTable.Header>
                  {table.getHeaderGroups().map(headerGroup => (
                    <DataTable.HeaderGroup key={headerGroup.id}>
                      {headerGroup.headers.map(header => (
                        <DataTable.ColumnHeaderCell key={header.id} header={header}>
                          {header.column.getCanSort() ? (
                            <DataTable.ColumnHeaderSort header={header} />
                          ) : null}
                        </DataTable.ColumnHeaderCell>
                      ))}
                    </DataTable.HeaderGroup>
                  ))}
                </DataTable.Header>
                <DataTable.Body>
                  {table.getRowModel().rows.map(row => (
                    <DataTable.Row key={row.id} row={row}>
                      {row.getVisibleCells().map(cell => (
                        <DataTable.Cell key={cell.id} cell={cell} />
                      ))}
                    </DataTable.Row>
                  ))}
                </DataTable.Body>
              </DataTable.Table>
              <DataTable.Pagination>
                <DataTable.PaginationPageNavigation />
                <DataTable.PaginationPageSize />
                <DataTable.PaginationTotal />
              </DataTable.Pagination>
            </>
          )}
        </DataTable.Root>
      </Skeleton>
    </Flex>
  );
};
